import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface RelatedRefDraft {
  id: string;
  title?: string;
}

/**
 * `kind: "relatedRefs"` — a node-level `related` list (`NodeRelatedRef[]`:
 * `packages/core/core/manifest.ts` — a bare string OR `{id,title?}`). Always
 * EMITS the object form (`{id,title?}`, `title` omitted when blank) — the schema
 * accepts both, so this stays valid without needing to reconstruct the bare-string
 * shorthand (docs/343 §3).
 */
export function RelatedRefsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const rows: RelatedRefDraft[] = Array.isArray(value)
    ? (value as unknown[]).map((v) => (typeof v === "string" ? { id: v } : (v as RelatedRefDraft)))
    : [];

  const commit = (next: RelatedRefDraft[]) => onChange(next.length > 0 ? next : undefined);
  const update = (i: number, patch: Partial<RelatedRefDraft>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => commit([...rows, { id: "" }]);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1}>
            <TextField
              size="small"
              label="Sibling node id"
              value={row.id}
              onChange={(e) => update(i, { id: e.target.value })}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="Title override (optional)"
              value={row.title ?? ""}
              onChange={(e) => update(i, { title: e.target.value || undefined })}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Button size="small" color="error" onClick={() => remove(i)}>
              Remove
            </Button>
          </Stack>
        ))}
        <Button size="small" variant="outlined" onClick={add} sx={{ alignSelf: "flex-start" }}>
          + Add related
        </Button>
      </Stack>
    </Box>
  );
}
